import { diagnostic } from '../../utils/diagnostics';
import { Capacitor } from '@capacitor/core';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import AppLayout from '../../components/Layout/AppLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import '../../styles/brand-typography.css';
import './ManageFamily.css';
import Input from '../../components/UI/Input';
import InfoTooltip from '../../components/UI/InfoTooltip';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';

import {
  useFamilyStatus,
} from '../../contexts/FamilyStatusContext';

import {
  useAuth,
} from '../../contexts/AuthContext';

import {
  db,
  supabase,
} from '../../services/supabase';

import {
  getAgeGroupLabel,
} from '../../utils/ageGroups';
import { copyTextToClipboard } from '../../utils/clipboard';


const guardianInviteOrigin = () => {
  if (!Capacitor.isNativePlatform()) return window.location.origin;
  return import.meta.env.VITE_APP_CHANNEL === 'parents_dev'
    ? 'https://pocketaccounting.netlify.app'
    : 'https://app.mypiggyapp.com';
};

const FamilyChildAvatar = ({ child }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [child.avatar_url]);
  let imageUrl = null;
  try {
    const url = new URL(child.avatar_url);
    if (url.protocol === 'https:' || url.protocol === 'http:') imageUrl = url.href;
  } catch { /* Use the emoji when there is no valid picture URL. */ }
  return imageUrl && !failed
    ? <img src={imageUrl} alt="" onError={() => setFailed(true)} className="h-10 w-10 shrink-0 rounded-full object-cover" />
    : <span className="text-3xl" aria-hidden="true">{child.avatar || '🧒'}</span>;
};

export default function ManageFamily() {

  const navigate =
    useNavigate();


  const {
    parent,
    signOut,
  } = useAuth();

  const {
    isFamilyInactive,
    status: familyLifecycleStatus,
    canWrite: familyAccessCanWrite,
    deletionScheduledFor,
    refreshFamilyStatus,
  } = useFamilyStatus();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;


  /*
   * ---------------------------------------------------------
   * FAMILY / CHILD STATE
   * ---------------------------------------------------------
   */

  const [
    familyCode,
    setFamilyCode,
  ] = useState('');


  const [
    familyName,
    setFamilyName,
  ] = useState('');


  const [
    familyNameDraft,
    setFamilyNameDraft,
  ] = useState('');


  const [
    isEditingFamilyName,
    setIsEditingFamilyName,
  ] = useState(false);


  const [
    savingFamilyName,
    setSavingFamilyName,
  ] = useState(false);


  const [
    children,
    setChildren,
  ] = useState([]);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    error,
    setError,
  ] = useState('');


  const [
    success,
    setSuccess,
  ] = useState('');


const [
    reactivatingChildId,
    setReactivatingChildId,
  ] = useState(null);

  const [
    showDeactivatedChildren,
    setShowDeactivatedChildren,
  ] = useState(false);


const [
    pinResetChild,
    setPinResetChild,
  ] = useState(null);


/*
   * ---------------------------------------------------------
   * FAMILY ADULT MEMBERS
   * ---------------------------------------------------------
   */

  const [
    familyAdults,
    setFamilyAdults,
  ] = useState([]);


  const [
    familyAdultsLoading,
    setFamilyAdultsLoading,
  ] = useState(true);


  const [
    familyAdultsError,
    setFamilyAdultsError,
  ] = useState('');

  /*
   * ---------------------------------------------------------
   * GUARDIAN INVITATION STATE
   * ---------------------------------------------------------
   */

  const [
    guardianEmail,
    setGuardianEmail,
  ] = useState('');


  const [
    guardianInvitation,
    setGuardianInvitation,
  ] = useState(null);


  const [
    guardianInviteLoading,
    setGuardianInviteLoading,
  ] = useState(false);


  const [
    guardianInviteAction,
    setGuardianInviteAction,
  ] = useState('');


  const [
    guardianInviteError,
    setGuardianInviteError,
  ] = useState('');


  const [
    guardianInviteSuccess,
    setGuardianInviteSuccess,
  ] = useState('');


  const [
    guardianInviteUrl,
    setGuardianInviteUrl,
  ] = useState('');


  const isOwner =
    parent?.role === 'owner';


  /*
  * ---------------------------------------------------------
  * FAMILY DELETION / REACTIVATION
  * ---------------------------------------------------------
  */

  const [
    familyLifecycleAction,
    setFamilyLifecycleAction,
  ] = useState('');

  const [
    familyLifecycleError,
    setFamilyLifecycleError,
  ] = useState('');

  const [
    familyLifecycleSuccess,
    setFamilyLifecycleSuccess,
  ] = useState('');

   /*
   * ---------------------------------------------------------
   * GUARDIAN LEAVE FAMILY
   * ---------------------------------------------------------
   */

  const [
    leavingFamily,
    setLeavingFamily,
  ] = useState(false);


  const [
    leaveFamilyError,
    setLeaveFamilyError,
  ] = useState('');

   /*
   * ---------------------------------------------------------
   * OWNER REMOVE GUARDIAN
   * ---------------------------------------------------------
   */

  const [
    removingGuardianId,
    setRemovingGuardianId,
  ] = useState(null);


  const [
    removeGuardianError,
    setRemoveGuardianError,
  ] = useState('');

  /*
   * ---------------------------------------------------------
   * Date Formatter Helper Function
   * ---------------------------------------------------------
   */
  
    const formatDeletionDate =
    (value) => {

      if (!value) {
        return '30 days after the deletion request';
      }

      const date =
        new Date(value);

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return '30 days after the deletion request';
      }

      return date.toLocaleDateString(
        undefined,
        {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }
      );
    };
  
  
  /*
   * ---------------------------------------------------------
   * LOAD FAMILY ADULT MEMBERS
   * ---------------------------------------------------------
   */

  const loadFamilyAdults =
    useCallback(
      async () => {

        if (!parent?.family_id) {
          setFamilyAdults([]);
          setFamilyAdultsLoading(false);

          return;
        }


        setFamilyAdultsLoading(true);
        setFamilyAdultsError('');


        try {

          const {
            data,
            error:
              adultsError,
          } =
            await supabase.rpc(
              'get_my_family_adults'
            );


          if (adultsError) {
            throw adultsError;
          }


          setFamilyAdults(
            Array.isArray(data)
              ? data
              : []
          );

        } catch (adultsError) {
          diagnostic('error', 'family.adults_load_failed');


          setFamilyAdults([]);


          setFamilyAdultsError(
            adultsError?.message ||
              'Unable to load Parents & Guardians.'
          );

        } finally {
          setFamilyAdultsLoading(false);
        }
      },
      [
        parent?.family_id,
      ]
    );


  /*
   * ---------------------------------------------------------
   * LOAD GUARDIAN INVITATION STATUS
   * ---------------------------------------------------------
   */

  const loadGuardianInvitation =
    useCallback(
      async ({
        quiet = false,
      } = {}) => {

        if (!isOwner) {
          setGuardianInvitation(
            null
          );

          return;
        }


        if (!quiet) {
          setGuardianInviteLoading(
            true
          );
        }


        setGuardianInviteError('');


        try {

          const {
            data,
            error:
              invitationError,
          } =
            await supabase.rpc(
              'get_guardian_invitation_status'
            );


          if (invitationError) {
            throw invitationError;
          }


          const invitation =
            Array.isArray(data) &&
            data.length > 0
              ? data[0]
              : null;


          setGuardianInvitation(
            invitation
          );


          if (
            invitation
              ?.invited_email
          ) {
            setGuardianEmail(
              invitation
                .invited_email
            );
          }

        } catch (inviteError) {
          diagnostic('error', 'family.invitation_status_failed');


          setGuardianInviteError(
            inviteError?.message ||
              'Unable to load Parent/Guardian invitation status.'
          );

        } finally {
          if (!quiet) {
            setGuardianInviteLoading(
              false
            );
          }
        }
      },
      [
        isOwner,
      ]
    );

  /*
   * Load adult family members independently so a problem here
   * does not prevent the rest of Manage Family from loading.
   */
  useEffect(() => {

    if (parent?.family_id) {
      loadFamilyAdults();
    }

  }, [
    parent?.family_id,
    loadFamilyAdults,
  ]);


  /*
   * ---------------------------------------------------------
   * INITIAL PAGE LOAD
   * ---------------------------------------------------------
   */

  useEffect(() => {

    const run =
      async () => {

        if (
          !parent?.family_id
        ) {
          return;
        }


        setLoading(true);
        setError('');


        try {

          const [
            family,
            kids,
          ] =
            await Promise.all([

              db.getFamily(
                parent.family_id
              ),

              db.getChildren(
                parent.family_id
              ),

            ]);


          setFamilyCode(
            family
              ?.kid_family_code ||
              ''
          );


          setFamilyName(
            family
              ?.display_name ||
              ''
          );


          setFamilyNameDraft(
            family
              ?.display_name ||
              ''
          );


          setChildren(
            kids || []
          );

        } catch (loadError) {
          diagnostic('error', 'family.settings_load_failed');


          setError(
            loadError?.message ||
              'Failed to load family settings'
          );

        } finally {
          setLoading(false);
        }
      };


    run();

  }, [
    parent?.family_id,
  ]);


  /*
   * Load invitation information separately so a problem with
   * invitation status does not prevent normal Manage Family
   * from loading.
   */
  useEffect(() => {

    if (isOwner) {
      loadGuardianInvitation();
    }

  }, [
    isOwner,
    loadGuardianInvitation,
  ]);


  /*
   * ---------------------------------------------------------
   * CREATE GUARDIAN INVITATION
   * ---------------------------------------------------------
   */

  const handleCreateGuardianInvitation =
    async (event) => {

      event.preventDefault();


      if (
        isWriteLocked ||
        guardianInviteAction
      ) {
        return;
      }


      const email =
        guardianEmail
          .trim()
          .toLowerCase();


      if (!email) {
        setGuardianInviteError(
          'Enter the email address of the Parent/Guardian you want to invite.'
        );

        return;
      }


      setGuardianInviteAction(
        'create'
      );

      setGuardianInviteError('');
      setGuardianInviteSuccess('');
      setGuardianInviteUrl('');


      try {

        const {
          data,
          error:
            functionError,
        } =
          await supabase.functions.invoke(
            'send_guardian_invitation',
            {
              body: {
                action:
                  'create',

                invitedEmail:
                  email,

                appOrigin:
                  guardianInviteOrigin(),
              },
            }
          );


          if (functionError) {
            let friendlyMessage =
              'Unable to send the Parent/Guardian invitation. Please try again.';

            try {
              const errorBody =
                await functionError.context?.json();

              const backendMessage =
                String(
                  errorBody?.error || ''
                ).trim();

              if (backendMessage) {
                const normalizedMessage =
                  backendMessage.toLowerCase();

                if (
                  normalizedMessage.includes(
                    'already belongs'
                  ) ||
                  normalizedMessage.includes(
                    'already connected'
                  ) ||
                  normalizedMessage.includes(
                    'already a member'
                  )
                ) {
                  friendlyMessage =
                    'This email is already connected to another MyPiggy family. They must leave that family before they can be invited here.';
                } else {
                  friendlyMessage =
                    backendMessage;
                }
              }
            } catch {
              // Keep the safe fallback message.
            }

            throw new Error(
              friendlyMessage
            );
          }


        if (!data?.success) {
          throw new Error(
            data?.error ||
              'Unable to send the Parent/Guardian invitation.'
          );
        }


        if (
          data?.inviteUrl
        ) {
          setGuardianInviteUrl(
            data.inviteUrl
          );
        }


        if (
          data?.emailSent
        ) {
          setGuardianInviteSuccess(
            `Invitation email sent to ${email}.`
          );

        } else {

          setGuardianInviteSuccess(
            'The invitation was created, but the email could not be sent. You can use Copy Link to send the invitation manually.'
          );


          if (
            data?.warning
          ) {
            diagnostic('warn', 'family.invitation_email_warning');
          }
        }


        await loadGuardianInvitation({
          quiet: true,
        });

      } catch (inviteError) {
        diagnostic('error', 'family.invitation_send_failed');


        setGuardianInviteError(
          inviteError?.message ||
            'Unable to send the Parent/Guardian invitation.'
        );

      } finally {
        setGuardianInviteAction('');
      }
    };


  /*
   * ---------------------------------------------------------
   * RESEND / ROTATE GUARDIAN INVITATION
   * ---------------------------------------------------------
   *
   * This creates a NEW token. The previous link becomes
   * invalid immediately.
   */

  const handleResendGuardianInvitation =
    async () => {

      const invitationId =
        guardianInvitation
          ?.invitation_id ||
        guardianInvitation
          ?.id;


      if (
        isWriteLocked ||
        !invitationId ||
        guardianInviteAction
      ) {
        return;
      }


      setGuardianInviteAction(
        'resend'
      );

      setGuardianInviteError('');
      setGuardianInviteSuccess('');
      setGuardianInviteUrl('');


      try {

        const {
          data,
          error:
            functionError,
        } =
          await supabase.functions.invoke(
            'send_guardian_invitation',
            {
              body: {
                action:
                  'resend',

                invitationId:
                  invitationId,

                appOrigin:
                  guardianInviteOrigin(),
              },
            }
          );


        if (functionError) {
          throw functionError;
        }


        if (!data?.success) {
          throw new Error(
            data?.error ||
              'Unable to resend the Parent/Guardian invitation.'
          );
        }


        if (
          data?.inviteUrl
        ) {
          setGuardianInviteUrl(
            data.inviteUrl
          );
        }


        if (
          data?.emailSent
        ) {
          setGuardianInviteSuccess(
            `A new invitation email was sent to ${
              data?.invitedEmail ||
              guardianInvitation?.invited_email ||
              'the Parent/Guardian'
            }. The previous invitation link is no longer valid.`
          );

        } else {

          setGuardianInviteSuccess(
            'A new invitation link was generated, but the email could not be sent. You can use Copy Link to send it manually.'
          );


          if (
            data?.warning
          ) {
            diagnostic('warn', 'family.invitation_resend_warning');
          }
        }


        await loadGuardianInvitation({
          quiet: true,
        });

      } catch (inviteError) {
        diagnostic('error', 'family.invitation_resend_failed');


        setGuardianInviteError(
          inviteError?.message ||
            'Unable to resend the Parent/Guardian invitation.'
        );

      } finally {
        setGuardianInviteAction('');
      }
    };


  /*
   * ---------------------------------------------------------
   * CANCEL GUARDIAN INVITATION
   * ---------------------------------------------------------
   */

  const handleCancelGuardianInvitation =
    async () => {

      const invitationId =
        guardianInvitation
          ?.invitation_id ||
        guardianInvitation
          ?.id;


      if (
        isWriteLocked ||
        !invitationId ||
        guardianInviteAction
      ) {
        return;
      }


      const confirmed =
        window.confirm(
          'Cancel this Parent/Guardian invitation?\n\n' +
          'The current invitation link will no longer be usable.'
        );


      if (!confirmed) {
        return;
      }


      setGuardianInviteAction(
        'cancel'
      );

      setGuardianInviteError('');
      setGuardianInviteSuccess('');
      setGuardianInviteUrl('');


      try {

        const {
          error:
            cancelError,
        } =
          await supabase.rpc(
            'cancel_guardian_invitation',
            {
              p_invitation_id:
                invitationId,
            }
          );


        if (cancelError) {
          throw cancelError;
        }


        setGuardianInvitation(
          null
        );

        setGuardianEmail('');


        setGuardianInviteSuccess(
          'Parent/Guardian invitation cancelled.'
        );


        await loadGuardianInvitation({
          quiet: true,
        });

      } catch (inviteError) {
        diagnostic('error', 'family.invitation_cancel_failed');


        setGuardianInviteError(
          inviteError?.message ||
            'Unable to cancel the Parent/Guardian invitation.'
        );

      } finally {
        setGuardianInviteAction('');
      }
    };


  /*
   * ---------------------------------------------------------
   * COPY GUARDIAN INVITATION LINK
   * ---------------------------------------------------------
   */

  const handleCopyGuardianInvitation =
    async () => {

      if (!guardianInviteUrl) {
        return;
      }


      try {

        await navigator
          .clipboard
          .writeText(
            guardianInviteUrl
          );


        setGuardianInviteSuccess(
          'Invitation link copied. You can now send it to the invited Parent/Guardian.'
        );


        setGuardianInviteError('');

      } catch {
        diagnostic('error', 'family.invitation_copy_failed');


        setGuardianInviteError(
          'Unable to copy the invitation link automatically. Please copy it manually.'
        );
      }
    };


  /*
   * ---------------------------------------------------------
   * CHILD CHORE APPROVAL
   * ---------------------------------------------------------
   */

  /*
   * ---------------------------------------------------------
   * COPY FAMILY CODE
   * ---------------------------------------------------------
   */

  const handleCopyFamilyCode =
    async () => {

      if (!familyCode) {
        return;
      }


      try {
        await navigator
          .clipboard
          .writeText(
            familyCode
          );

      } catch {
        diagnostic('error', 'family.code_copy_failed');
      }
    };


  /*
   * ---------------------------------------------------------
   * FAMILY DISPLAY NAME
   * ---------------------------------------------------------
   */

  const handleCancelFamilyNameEdit =
    () => {
      setFamilyNameDraft(
        familyName
      );
      setIsEditingFamilyName(false);
      setError('');
    };


  const handleSaveFamilyName =
    async (event) => {
      event.preventDefault();

      if (
        !isOwner ||
        isWriteLocked ||
        savingFamilyName
      ) {
        return;
      }

      const nextName =
        familyNameDraft.trim();

      setError('');
      setSuccess('');

      if (!nextName) {
        setError(
          'Family name is required.'
        );
        return;
      }

      if (
        nextName.length > 20
      ) {
        setError(
          'Family name must be 20 characters or fewer.'
        );
        return;
      }

      if (
        /^family$/i.test(nextName) ||
        /\s+family$/i.test(nextName)
      ) {
        setError(
          'Enter the family name without the word “Family.”'
        );
        return;
      }

      setSavingFamilyName(true);

      try {
        const savedName =
          await db.updateMyFamilyDisplayName(
            nextName
          );

        setFamilyName(
          savedName || nextName
        );
        setFamilyNameDraft(
          savedName || nextName
        );
        setIsEditingFamilyName(false);
        setSuccess(
          `${savedName || nextName} Family is now your family name. Your Family Code and kid Login IDs have not changed.`
        );
      } catch (saveError) {
        diagnostic('error', 'family.name_update_failed');

        setError(
          saveError?.message ||
            'Unable to update the family name.'
        );
      } finally {
        setSavingFamilyName(false);
      }
    };


  /*
   * ---------------------------------------------------------
   * COPY KID LOGIN ID
   * ---------------------------------------------------------
   */

  const handleCopyUserId =
    async (userId) => {

      if (!userId) {
        return;
      }


      try {
        const copied =
          await copyTextToClipboard(userId);

        if (!copied) {
          throw new Error(
            'Your browser did not allow access to the clipboard.'
          );
        }

        setError('');
        setSuccess('Kid Login ID copied.');

      } catch (copyError) {
        diagnostic('error', 'family.kid_login_id_copy_failed');
        setSuccess('');
        setError(
          copyError?.message ||
            'Unable to copy the Kid Login ID.'
        );
      }
    };


  /*
   * ---------------------------------------------------------
   * DEACTIVATE CHILD
   * ---------------------------------------------------------
   */

  const handleLeaveFamily =
    async () => {

      if (
        isFamilyInactive ||
        isOwner ||
        leavingFamily
      ) {
        return;
      }


      const confirmed =
        window.confirm(
          'Leave this MyPiggy family?\n\n' +
          'You will immediately lose access to this family, including its children, transactions, savings, chores, and reports.\n\n' +
          'The family and its data will NOT be deleted. Your MyPiggy account will also remain available.\n\n' +
          'You will be signed out after leaving.'
        );


      if (!confirmed) {
        return;
      }


      setLeavingFamily(true);
      setLeaveFamilyError('');


      try {

        const {
          data,
          error:
            leaveError,
        } =
          await supabase.rpc(
            'leave_family_as_guardian'
          );


        if (leaveError) {
          throw leaveError;
        }


        if (data !== true) {
          throw new Error(
            'Unable to leave the family.'
          );
        }


        /*
         * Membership has now been removed at the database
         * level. Sign out immediately so the former Guardian
         * cannot continue using the current parent session.
         */
        await signOut();


        navigate(
          '/parent/login',
          {
            replace: true,
          }
        );

      } catch (leaveError) {
        diagnostic('error', 'family.leave_failed');


        setLeaveFamilyError(
          leaveError?.message ||
            'Unable to leave the family. Please try again.'
        );


        setLeavingFamily(false);
      }
    };
  /*
   * ---------------------------------------------------------
   * Family Deactivation Code
   * ---------------------------------------------------------
   */


    const handleRequestFamilyDeletion =
    async () => {

      if (
        !isOwner ||
        isFamilyInactive ||
        familyLifecycleAction
      ) {
        return;
      }


      const confirmed =
        window.confirm(
          'Delete this MyPiggy family?\n\n' +
          'Your family will become inactive immediately and all family information will become read-only.\n\n' +
          'You, your Parent/Guardian, and your kids will still be able to sign in and view existing information during the 30-day recovery period.\n\n' +
          'You can reactivate the family during that period. After 30 days, the family will be eligible for permanent deletion.\n\n' +
          'Continue with family deletion?'
        );


      if (!confirmed) {
        return;
      }


      setFamilyLifecycleAction(
        'deactivate'
      );

      setFamilyLifecycleError('');
      setFamilyLifecycleSuccess('');


      try {

        const {
          data,
          error:
            deactivateError,
        } =
          await supabase.rpc(
            'request_family_deletion'
          );


        if (deactivateError) {
          throw deactivateError;
        }


        diagnostic('info', 'family.deletion_requested');


        await refreshFamilyStatus();


        setFamilyLifecycleSuccess(
          'Your family is now inactive and read-only. You can reactivate it during the 30-day recovery period.'
        );

      } catch (deactivateError) {

        diagnostic('error', 'family.deletion_failed');


        setFamilyLifecycleError(
          deactivateError?.message ||
            'Unable to deactivate this family. Please try again.'
        );

      } finally {

        setFamilyLifecycleAction('');
      }
    };


  const handleReactivateFamily =
    async () => {

      if (
        !isOwner ||
        !isFamilyInactive ||
        familyLifecycleAction
      ) {
        return;
      }


      const confirmed =
        window.confirm(
          'Reactivate this MyPiggy family?\n\n' +
          'Normal family activity will be restored and the scheduled deletion will be cancelled.'
        );


      if (!confirmed) {
        return;
      }


      setFamilyLifecycleAction(
        'reactivate'
      );

      setFamilyLifecycleError('');
      setFamilyLifecycleSuccess('');


      try {

        const {
          data,
          error:
            reactivateError,
        } =
          await supabase.rpc(
            'reactivate_family'
          );


        if (reactivateError) {
          throw reactivateError;
        }


        diagnostic('info', 'family.reactivation_requested');


        await refreshFamilyStatus();


        setFamilyLifecycleSuccess(
          'Your family has been reactivated. Normal family activity is available again.'
        );

      } catch (reactivateError) {

        diagnostic('error', 'family.reactivation_failed');


        setFamilyLifecycleError(
          reactivateError?.message ||
            'Unable to reactivate this family. Please try again.'
        );

      } finally {

        setFamilyLifecycleAction('');
      }
    };
        /*
   * ---------------------------------------------------------
   * Family Deactivation Code
   * ---------------------------------------------------------
   */

      /*
   * ---------------------------------------------------------
   * OWNER REMOVE GUARDIAN
   * ---------------------------------------------------------
   */

  const handleRemoveGuardian =
    async (adult) => {

      if (
        isWriteLocked ||
        !isOwner ||
        !adult?.parent_id ||
        adult.member_role !==
          'guardian' ||
        removingGuardianId
      ) {
        return;
      }


      const guardianName =
        adult.display_name ||
        adult.email ||
        'this Parent/Guardian';


      const confirmed =
        window.confirm(
          `Remove ${guardianName} from this MyPiggy family?\n\n` +
          'They will immediately lose access to this family, including its children, transactions, savings, chores, and reports.\n\n' +
          'The family and its data will NOT be deleted. Their MyPiggy account will also remain available.'
        );


      if (!confirmed) {
        return;
      }


      setRemovingGuardianId(
        adult.parent_id
      );

      setRemoveGuardianError('');

      setGuardianInviteError('');
      setGuardianInviteSuccess('');


      try {

        const {
          data,
          error:
            removeError,
        } =
          await supabase.rpc(
            'remove_family_guardian',
            {
              p_guardian_parent_id:
                adult.parent_id,
            }
          );


        if (removeError) {
          throw removeError;
        }


        if (data !== true) {
          throw new Error(
            'Unable to remove the Parent/Guardian.'
          );
        }


        /*
         * Refresh both views because removing the Guardian
         * changes:
         *
         * 1. the active Parents & Guardians list
         * 2. the current Guardian invitation/access state
         */
        await Promise.all([
          loadFamilyAdults(),

          loadGuardianInvitation({
            quiet: true,
          }),
        ]);


        setGuardianEmail('');


        setGuardianInviteSuccess(
          `${guardianName} was removed from this family. You can invite another Parent/Guardian when ready.`
        );

      } catch (removeError) {
        diagnostic('error', 'family.guardian_remove_failed');


        setRemoveGuardianError(
          removeError?.message ||
            'Unable to remove this Parent/Guardian. Please try again.'
        );

      } finally {
        setRemovingGuardianId(
          null
        );
      }
    };

  /*
   * ---------------------------------------------------------
   * FORMAT INVITATION DATE
   * ---------------------------------------------------------
   */

  const formatInvitationDate =
    (value) => {

      if (!value) {
        return '—';
      }


      const date =
        new Date(value);


      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return '—';
      }


      return date.toLocaleString();
    };

  const activeChildren = children.filter(
    (child) => child.status === 'active'
  );

  const deactivatedChildren = children.filter(
    (child) => child.status === 'archived'
  );

  const renderChild = (child) => (
    <div key={child.id} className="flex flex-col rounded-xl border border-gray-200 p-3 dark:border-slate-600">
      <div className="flex items-start gap-3">
        <FamilyChildAvatar child={child} />
        <div className="min-w-0 flex-1">
          <h3 className="break-words font-extrabold">{child.nickname || child.full_name || 'Child'}</h3>
          {child.full_name && <p className="text-sm text-gray-600 dark:text-slate-300">{child.full_name}</p>}
          <p className="mt-1 text-xs text-gray-600 dark:text-slate-300">{getAgeGroupLabel(child.age_group)}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-bold ${child.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300' : 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300'}`}>{child.status === 'active' ? 'Active' : 'Deactivated'}</span>
      </div>
      <div className="my-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-slate-300">
        <span>MyPiggy Login ID: <strong className="break-all text-blue-700 dark:text-blue-300">{child.user_id || 'Not assigned'}</strong></span>
        {child.user_id && <button type="button" onClick={() => handleCopyUserId(child.user_id)} className="rounded px-1 py-2 font-bold text-blue-700 underline dark:text-blue-300" aria-label={`Copy Login ID for ${child.nickname || child.full_name}`}>Copy</button>}
      </div>
      <Button variant="secondary" className="mt-auto w-full" onClick={() => navigate(`/parent/children/${child.id}`, { state: { from: '/parent/manage-family' } })}>Manage Child →</Button>
    </div>
  );

  /*
   * =========================================================
   * PAGE
   * =========================================================
   */

  return (
    <AppLayout
      title="Manage Family"
      className="mypiggy-brand manage-family"
      showBack
      onBack={() =>
        navigate(
          '/parent'
        )
      }
    >

      <div className="space-y-4">


        {loading ? (

          <div className="text-center py-8">
            Loading...
          </div>

        ) : (

          <>


            {error && (
              <Card className="p-4 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/35">

                <div className="text-red-700 dark:text-red-300 font-medium">
                  {error}
                </div>

              </Card>
            )}


            {success && (
              <Card className="p-4 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/35">

                <div className="text-green-700 dark:text-green-300 font-medium">
                  {success}
                </div>

              </Card>
            )}


            {/* =================================================
                FAMILY NAME
               ================================================= */}

            <div className="grid items-stretch gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Card className="p-4 sm:p-5">

              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

                <div className="min-w-0 flex-1">

                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100">
                      Family Name
                    </h2>

                    <InfoTooltip compact
                      text="Your family name is used only for personalized headings. It never changes the Family Code or any kid Login ID."
                      ariaLabel="About the family name"
                    />
                  </div>


                  {isEditingFamilyName && isOwner ? (

                    <form
                      onSubmit={
                        handleSaveFamilyName
                      }
                      className="mt-4 max-w-md space-y-3"
                    >
                      <Input
                        label="Family Name"
                        value={
                          familyNameDraft
                        }
                        onChange={(event) =>
                          setFamilyNameDraft(
                            event.target.value
                          )
                        }
                        maxLength={20}
                        autoComplete="off"
                        helperText="Enter the family name only—for example, Parikh. We’ll add “Family” automatically."
                        disabled={
                          savingFamilyName ||
                          isWriteLocked
                        }
                        autoFocus
                        required
                      />

                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={
                            savingFamilyName ||
                            isWriteLocked
                          }
                        >
                          {savingFamilyName
                            ? 'Saving...'
                            : 'Save Family Name'}
                        </Button>

                        <Button
                          type="button"
                          variant="secondary"
                          onClick={
                            handleCancelFamilyNameEdit
                          }
                          disabled={
                            savingFamilyName
                          }
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>

                  ) : (

                    <div className="mt-3">
                      <div className="text-xl font-bold text-gray-900 dark:text-slate-100 break-words">
                        {familyName
                          ? `${familyName} Family`
                          : 'Family name not set'}
                      </div>

                      <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">
                        {familyName
                          ? 'Used for personalized family headings throughout MyPiggy.'
                          : isOwner
                            ? 'Set a short family name to personalize your dashboard.'
                            : 'The Family Organizer has not set a family name yet.'}
                      </p>
                    </div>

                  )}

                </div>


                {isOwner &&
                  !isEditingFamilyName && (

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setFamilyNameDraft(
                        familyName
                      );
                      setError('');
                      setSuccess('');
                      setIsEditingFamilyName(true);
                    }}
                    disabled={
                      isWriteLocked
                    }
                  >
                    {familyName
                      ? 'Edit Family Name'
                      : 'Set Family Name'}
                  </Button>

                )}

              </div>


              <div className="mt-3 text-sm">
                <p className="text-sm text-emerald-800 dark:text-emerald-300">
                  Family Code and kids’ Login IDs stay the same when you change this name.
                </p>
              </div>

            </Card>


            {/* =================================================
                KID FAMILY CODE
               ================================================= */}

            <Card className="p-4 sm:p-5 bg-blue-50 dark:bg-blue-950/35 border-blue-200 dark:border-blue-800">

              <div className="flex items-start justify-between gap-4">

                <div>

                  <h3 className="font-semibold text-blue-900 dark:text-blue-200">
                    Family Code
                  </h3>


                  <p className="text-blue-700 dark:text-blue-300 text-sm">
                    The permanent prefix in your kids’ MyPiggy Login IDs.
                  </p>

                </div>


                <div className="text-right">

                  <div className="text-2xl font-extrabold tracking-widest text-blue-900 dark:text-blue-200">
                    {familyCode ||
                      '—'}
                  </div>


                  {familyCode && (
                    <button
                      type="button"
                      className="text-sm text-blue-700 dark:text-blue-300 underline mt-1"
                      onClick={
                        handleCopyFamilyCode
                      }
                    >
                      Copy
                    </button>
                  )}

                </div>

              </div>

            </Card>

            </div>

                        {/* =================================================
                PARENTS & GUARDIANS
               ================================================= */}

            <Card className="p-4 sm:p-5">

              <div className="flex items-start justify-between gap-4 mb-3">

                <div>

                  <div className="flex items-center gap-2">

                    <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100">
                      Parents & Guardians
                    </h2>


                    <InfoTooltip compact
                      text="These are the adults who currently have access to this MyPiggy family."
                      ariaLabel="About Parents and Guardians"
                    />

                  </div>


                  <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                    Adults who can help manage your family's MyPiggy account.
                  </p>

                </div>


                {!familyAdultsLoading && (
                  <span className="shrink-0 inline-flex rounded-full bg-gray-100 dark:bg-slate-800 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:text-slate-300">
                    {familyAdults.length}{' '}
                    {familyAdults.length === 1
                      ? 'Adult'
                      : 'Adults'}
                  </span>
                )}

              </div>


              {familyAdultsError && (

                <div
                  className="mb-4 rounded-lg border border-red-200 dark:border-red-900/70 bg-red-50 dark:bg-red-950/35 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                  role="alert"
                >
                  {familyAdultsError}
                </div>

              )}

              {removeGuardianError && (

                <div
                  className="mb-4 rounded-lg border border-red-200 dark:border-red-900/70 bg-red-50 dark:bg-red-950/35 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                  role="alert"
                >
                  {removeGuardianError}
                </div>

              )}

              {familyAdultsLoading ? (

                <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-5 text-sm text-gray-500 dark:text-slate-400">
                  Loading Parents & Guardians...
                </div>

              ) : familyAdults.length === 0 ? (

                <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-5 text-sm text-gray-500 dark:text-slate-400">
                  No family adults could be loaded.
                </div>

              ) : (

                <div className="space-y-3">

                  {familyAdults.map(
                    (
                      adult
                    ) => {

                      const adultIsOwner =
                        adult.member_role ===
                        'owner';

                      const canRemoveGuardian =
                        isOwner &&
                        !adultIsOwner &&
                        adult.member_role ===
                          'guardian';


                      const isRemovingGuardian =
                        removingGuardianId ===
                          adult.parent_id;


                      return (

                        <div
                          key={
                            adult.parent_id
                          }
                          className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 p-4"
                        >

                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">

                            <div className="min-w-0">

                              <div className="flex flex-wrap items-center gap-2">

                                <p className="font-semibold text-gray-900 dark:text-slate-100">
                                  {adult.display_name ||
                                    'Parent/Guardian'}
                                </p>


                                {adult.is_current_user && (

                                  <span className="inline-flex rounded-full bg-green-100 dark:bg-green-950/50 px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-300">
                                    You
                                  </span>

                                )}

                              </div>


                              <p className="mt-1 text-sm text-gray-600 dark:text-slate-300 break-all">
                                {adult.email ||
                                  '—'}
                              </p>


                              <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                                {adultIsOwner
                                  ? 'Family Organizer'
                                  : 'Parent / Guardian'}
                              </p>

                            </div>


                            <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">

                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  adultIsOwner
                                    ? 'bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300'
                                    : 'bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300'
                                }`}
                              >
                                {adultIsOwner
                                  ? 'ORGANIZER'
                                  : 'GUARDIAN'}
                              </span>


                              {canRemoveGuardian && (

                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveGuardian(
                                      adult
                                    )
                                  }
                                  disabled={
                                    isWriteLocked ||
                                    Boolean(
                                      removingGuardianId
                                    )
                                  }
                                  className="rounded-lg border border-red-200 dark:border-red-800 px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/35 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isRemovingGuardian
                                    ? 'Removing...'
                                    : 'Remove Guardian'}
                                </button>

                              )}

                            </div>

                          </div>

                        </div>

                      );
                    }
                  )}

                </div>

              )}


              {!isOwner && (

                <div className="mt-4 space-y-3">

                  <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30 px-4 py-3">

                    <p className="text-sm font-medium text-violet-900 dark:text-violet-200">
                      You are a Parent/Guardian of this family.
                    </p>


                    <p className="mt-1 text-xs text-violet-700 dark:text-violet-300">
                      You can help manage the family's children, transactions, savings, chores, and settings.
                    </p>

                  </div>


                  {leaveFamilyError && (

                    <div
                      className="rounded-lg border border-red-200 dark:border-red-900/70 bg-red-50 dark:bg-red-950/35 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                      role="alert"
                    >
                      {leaveFamilyError}
                    </div>

                  )}


                  <div className="rounded-xl border border-red-200 dark:border-red-900/70 bg-red-50/60 dark:bg-red-950/20 px-4 py-4">

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

                      <div>

                        <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                          Leave Family
                        </p>


                        <p className="mt-1 text-xs text-gray-600 dark:text-slate-300">
                          Remove your Parent/Guardian access to this family. The family and its data will not be deleted.
                        </p>

                      </div>


                      <button
                        type="button"
                        onClick={
                          handleLeaveFamily
                        }
                        disabled={
                          isFamilyInactive ||
                          leavingFamily
                        }
                        className="shrink-0 rounded-lg border border-red-300 dark:border-red-800 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {leavingFamily
                          ? 'Leaving...'
                          : 'Leave Family'}
                      </button>

                    </div>

                  </div>

                </div>

              )}

            </Card>

            {/* =================================================
                PARENT / GUARDIAN ACCESS
                OWNER ONLY
               ================================================= */}

            {isOwner && (

              <Card className="p-4 sm:p-5">

                <div className="flex items-start justify-between gap-4 mb-3">

                  <div>

                    <div className="flex items-center gap-2">

                      <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100">
                        Parent/Guardian Access
                      </h2>


                      <InfoTooltip compact
                        text="The Family Organizer can invite one additional Parent/Guardian to help manage the same MyPiggy family."
                        ariaLabel="About Parent/Guardian access"
                      />

                    </div>


                    <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                      Invite one additional adult to access and manage this family.
                    </p>

                  </div>


                  <span className="shrink-0 inline-flex rounded-full bg-blue-100 dark:bg-blue-950/50 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300">
                    Organizer Only
                  </span>

                </div>


                {guardianInviteError && (

                  <div
                    className="mb-4 rounded-lg border border-red-200 dark:border-red-900/70 bg-red-50 dark:bg-red-950/35 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                    role="alert"
                  >
                    {guardianInviteError}
                  </div>

                )}


                {guardianInviteSuccess && (

                  <div
                    className="mb-4 rounded-lg border border-green-200 dark:border-green-900/70 bg-green-50 dark:bg-green-950/35 px-4 py-3 text-sm text-green-700 dark:text-green-300"
                    role="status"
                  >
                    {guardianInviteSuccess}
                  </div>

                )}


                {guardianInviteLoading ? (

                  <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-5 text-sm text-gray-500 dark:text-slate-400">
                    Loading Parent/Guardian invitation...
                  </div>

                ) : guardianInvitation ? (

                  <div className="space-y-4">

                    <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 p-5">

                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

                        <div>

                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                            Invited Parent/Guardian
                          </p>


                          <p className="mt-1 font-semibold text-gray-900 dark:text-slate-100 break-all">
                            {guardianInvitation
                              .invited_email}
                          </p>


                          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                            Status
                          </p>


                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-slate-100 capitalize">
                            {guardianInvitation
                              .status ||
                              'pending'}
                          </p>

                        </div>


                        <div className="sm:text-right">

                          {guardianInvitation.status === 'pending' && (
                            <>
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                                Expires
                              </p>

                              <p className="mt-1 text-sm text-gray-700 dark:text-slate-300">
                                {formatInvitationDate(
                                  guardianInvitation.expires_at
                                )}
                              </p>
                            </>
                          )}

                          {guardianInvitation.status === 'accepted' && (
                            <>
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                                Accepted On
                              </p>

                              <p className="mt-1 text-sm text-gray-700 dark:text-slate-300">
                                {formatInvitationDate(
                                  guardianInvitation.accepted_at
                                )}
                              </p>
                            </>
                          )}


                          {guardianInvitation
                            .send_count != null && (

                            <>
                              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                                Links Generated
                              </p>

                              <p className="mt-1 text-sm text-gray-700 dark:text-slate-300">
                                {guardianInvitation
                                  .send_count}
                              </p>
                            </>

                          )}

                        </div>

                      </div>

                    </div>


                    {guardianInviteUrl && (

                      <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/35 p-4">

                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                          Invitation Link
                        </p>


                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Send this private link only to the invited Parent/Guardian.
                        </p>


                        <div className="mt-3 flex flex-col gap-2 sm:flex-row">

                          <input
                            type="text"
                            readOnly
                            value={
                              guardianInviteUrl
                            }
                            className="min-w-0 flex-1 rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-slate-900 px-3 py-2 text-xs text-gray-700 dark:text-slate-300"
                            aria-label="Guardian invitation link"
                          />


                          <Button
                            type="button"
                            variant="primary"
                            onClick={
                              handleCopyGuardianInvitation
                            }
                          >
                            Copy Link
                          </Button>

                        </div>

                      </div>

                    )}


                    {guardianInvitation.status === 'pending' && (
                      <div className="flex flex-col gap-3 sm:flex-row">

                        <Button
                          type="button"
                          variant="secondary"
                          onClick={
                            handleResendGuardianInvitation
                          }
                          disabled={
                            isWriteLocked ||
                            Boolean(
                              guardianInviteAction
                            )
                          }
                        >
                          {guardianInviteAction === 'resend'
                            ? 'Resending...'
                            : 'Resend Invitation'}
                        </Button>

                        <button
                          type="button"
                          onClick={
                            handleCancelGuardianInvitation
                          }
                          disabled={
                            isWriteLocked ||
                            Boolean(
                              guardianInviteAction
                            )
                          }
                          className="rounded-lg border border-red-200 dark:border-red-800 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/35 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {guardianInviteAction === 'cancel'
                            ? 'Cancelling...'
                            : 'Cancel Invitation'}
                        </button>

                      </div>
                    )}


                    {guardianInvitation.status === 'pending' &&
                      !guardianInviteUrl && (

                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        For security, the private invitation link is shown only when an invitation is first sent or resent. MyPiggy does not store the original invitation token.
                      </p>

                    )}

                  </div>

                ) : (

                  <form
                    onSubmit={
                      handleCreateGuardianInvitation
                    }
                    className="space-y-4"
                  >

                    <div>

                      <Input
                        label="Parent/Guardian Email"
                        type="email"
                        value={
                          guardianEmail
                        }
                        onChange={(
                          event
                        ) => {
                          setGuardianEmail(
                            event
                              .target
                              .value
                          );

                          setGuardianInviteError('');
                          setGuardianInviteSuccess('');
                        }}
                        placeholder="parent@example.com"
                        autoComplete="email"
                        disabled={
                          isWriteLocked ||
                          Boolean(
                            guardianInviteAction
                          )
                        }
                        required
                      />


                      <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                        This person will join your existing family as a Parent/Guardian. They will not receive a separate family.
                      </p>

                    </div>


                    <Button
                      type="submit"
                      variant="primary"
                      disabled={
                        isWriteLocked ||
                        Boolean(
                          guardianInviteAction
                        )
                      }
                    >
                      {guardianInviteAction ===
                      'create'
                        ? 'Sending Invitation...'
                        : 'Send Invitation'}
                    </Button>

                  </form>

                )}

              </Card>

            )}


            {/* =================================================
                CHILDREN
               ================================================= */}

            <Card className="p-4 sm:p-5">

              <div className="flex items-center justify-between mb-4">

                <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100">
                  Children
                </h2>


                <Button
                  variant="primary"
                  onClick={() =>
                    navigate(
                      '/parent/add-child'
                    )
                  }
                  disabled={
                    isWriteLocked
                  }
                  title={
                    isWriteLocked
                      ? 'Unavailable while this family is read-only.'
                      : undefined
                  }
                >
                  + Add Child
                </Button>

              </div>


              <div className="grid gap-3 sm:grid-cols-2">
                {activeChildren.map((child) => renderChild(child))}
              </div>
              {activeChildren.length === 0 && <p className="text-sm text-gray-600 dark:text-slate-300">No active children yet.</p>}
              {deactivatedChildren.length > 0 && (
                <div className="mt-3">
                  <button type="button" onClick={() => setShowDeactivatedChildren((value) => !value)} aria-expanded={showDeactivatedChildren}
                    className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-left text-sm font-bold dark:border-slate-600 dark:bg-slate-900/50">
                    Deactivated Children ({deactivatedChildren.length}) <span aria-hidden="true">{showDeactivatedChildren ? '−' : '+'}</span>
                  </button>
                  {showDeactivatedChildren && <div className="mt-3 grid gap-3 sm:grid-cols-2">{deactivatedChildren.map((child) => renderChild(child))}</div>}
                </div>
              )}

            </Card>


            {/* =================================================
                MORE
               ================================================= */}

            <Card className="p-4 sm:p-5">

              <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100 mb-3">
                More
              </h2>


              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {[
                  { title: 'Manage Chores', icon: 'do-chores', route: '/parent/chores/manage', color: 'amber' },
                  { title: 'Family Reports', icon: 'view-my-reports', route: '/parent/reports', color: 'violet' },
                  { title: 'View All Transactions', icon: 'transaction-history', route: '/parent/transactions', color: 'sky' },
                ].map((action) => (
                  <button key={action.route} type="button" onClick={() => navigate(action.route)}
                    className={`family-shortcut family-shortcut--${action.color} flex min-h-24 flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-2 py-3 text-center dark:border-slate-600 dark:bg-slate-900/50 sm:min-h-0 sm:flex-row sm:gap-3 sm:px-3 sm:py-4`}>
                    <MyPiggyIcon name={action.icon} size="sm" />
                    <span className="text-xs font-extrabold sm:text-sm">{action.title}</span>
                  </button>
                ))}
              </div>

            </Card>

                        {/* =================================================
              FAMILY ACCOUNT
              OWNER ONLY
            ================================================= */}

            {isOwner && (

              <Card
                className={
                  isFamilyInactive
                    ? 'p-4 sm:p-5 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/25'
                    : 'p-4 sm:p-5 border-red-200 dark:border-red-900/70'
                }
              >

                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

                  <div className="max-w-2xl">

                    <div className="flex flex-wrap items-center gap-2">

                      <h2
                        className={
                          isFamilyInactive
                            ? 'text-lg font-extrabold text-amber-900 dark:text-amber-200'
                            : 'text-lg font-extrabold text-gray-900 dark:text-slate-100'
                        }
                      >
                        {isFamilyInactive
                          ? 'Family Access'
                          : 'Danger Zone'}
                      </h2>


                      <span className="inline-flex rounded-full bg-blue-100 dark:bg-blue-950/50 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300">
                        Organizer Only
                      </span>

                    </div>


                    {isFamilyInactive ? (

                      <>
                        <p className="mt-2 text-sm text-amber-900 dark:text-amber-200">
                          This family is currently read-only.
                        </p>


                        {deletionScheduledFor && <p className="mt-2 text-sm text-gray-700 dark:text-slate-300">
                          The family is scheduled for permanent deletion on{' '}
                          <strong>
                            {formatDeletionDate(
                              deletionScheduledFor
                            )}
                          </strong>.
                        </p>}


                        <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
                          {familyLifecycleStatus === 'admin_read_only'
                            ? 'MyPiggy administration placed this family on hold. Contact support; the Family Organizer cannot remove this hold.'
                            : deletionScheduledFor && new Date(deletionScheduledFor) > new Date()
                              ? 'The Family Organizer can reactivate during the recovery period.'
                              : 'The recovery period has ended. Contact MyPiggy support.'}
                        </p>
                      </>

                    ) : (

                      <>
                        <p className="mt-2 text-sm text-gray-700 dark:text-slate-300">
                          Deleting your family begins a 30-day recovery period.
                        </p>


                        <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
                          The family becomes read-only immediately. Parents, Guardians, and kids can still sign in and view existing information, but no family activity can be changed.
                        </p>


                        <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
                          You can reactivate the family during the recovery period. After 30 days, the family becomes eligible for permanent deletion.
                        </p>
                      </>

                    )}

                  </div>


                  <div className="shrink-0">

                    {isFamilyInactive && familyLifecycleStatus === 'inactive' &&
                      deletionScheduledFor && new Date(deletionScheduledFor) > new Date() ? (

                      <Button
                        variant="primary"
                        onClick={
                          handleReactivateFamily
                        }
                        disabled={
                          Boolean(
                            familyLifecycleAction
                          )
                        }
                      >
                        {familyLifecycleAction ===
                        'reactivate'
                          ? 'Reactivating...'
                          : 'Reactivate Family'}
                      </Button>

                    ) : !isFamilyInactive ? (

                      <button
                        type="button"
                        onClick={
                          handleRequestFamilyDeletion
                        }
                        disabled={
                          Boolean(
                            familyLifecycleAction
                          )
                        }
                        className="
                          rounded-lg
                          border
                          border-red-600
                          bg-red-600
                          px-4
                          py-2
                          text-sm
                          font-semibold
                          text-white
                          transition
                          hover:bg-red-700
                          disabled:cursor-not-allowed
                          disabled:opacity-50
                          dark:border-red-500
                          dark:bg-red-600
                          dark:hover:bg-red-700
                        "
                      >
                        {familyLifecycleAction ===
                        'deactivate'
                          ? 'Starting Deletion...'
                          : 'Delete Family'}
                      </button>

                    ) : null}

                  </div>

                </div>


                {familyLifecycleError && (

                  <div
                    className="mt-4 rounded-lg border border-red-200 dark:border-red-900/70 bg-red-50 dark:bg-red-950/35 px-4 py-3 text-sm text-red-700 dark:text-red-300"
                    role="alert"
                  >
                    {familyLifecycleError}
                  </div>

                )}


                {familyLifecycleSuccess && (

                  <div
                    className="mt-4 rounded-lg border border-green-200 dark:border-green-900/70 bg-green-50 dark:bg-green-950/35 px-4 py-3 text-sm text-green-700 dark:text-green-300"
                    role="status"
                  >
                    {familyLifecycleSuccess}
                  </div>

                )}

              </Card>

            )}

          </>

        )}

      </div>


    </AppLayout>
  );
}

